import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import RecordMetadata from '@/components/RecordMetadata';
import type { AuditProfile } from '@prolarus/shared';

const uomSchema = z.object({
  uom: z.string().min(1, 'UoM code is required'),
  singular: z.string().min(1, 'Singular form is required'),
  plural: z.string().min(1, 'Plural form is required'),
  description: z.string().optional(),
  is_si: z.boolean(),
  is_imperial: z.boolean(),
  is_active: z.boolean(),
});

type UoMFormValues = z.infer<typeof uomSchema>;

export interface UoMRecord {
  id: string;
  uom: string;
  singular: string;
  plural: string;
  description: string | null;
  is_si: boolean;
  is_imperial: boolean;
  is_active: boolean;
  created?: string | null;
  modified?: string | null;
  created_by_profile?: AuditProfile | null;
  updated_by_profile?: AuditProfile | null;
}

interface UoMFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uom: UoMRecord | null;
  onSave: (data: Omit<UoMRecord, 'id'>) => Promise<void>;
}

export default function UoMFormDialog({ open, onOpenChange, uom, onSave }: UoMFormDialogProps) {
  const isEditing = !!uom;

  const form = useForm<UoMFormValues>({
    resolver: zodResolver(uomSchema),
    defaultValues: {
      uom: '',
      singular: '',
      plural: '',
      description: '',
      is_si: false,
      is_imperial: false,
      is_active: true,
    },
  });

  useEffect(() => {
    if (open) {
      if (uom) {
        form.reset({
          uom: uom.uom,
          singular: uom.singular,
          plural: uom.plural,
          description: uom.description || '',
          is_si: uom.is_si,
          is_imperial: uom.is_imperial,
          is_active: uom.is_active,
        });
      } else {
        form.reset({
          uom: '',
          singular: '',
          plural: '',
          description: '',
          is_si: false,
          is_imperial: false,
          is_active: true,
        });
      }
    }
  }, [open, uom, form]);

  const onSubmit = async (values: UoMFormValues) => {
    await onSave({
      uom: values.uom,
      singular: values.singular,
      plural: values.plural,
      description: values.description || null,
      is_si: values.is_si,
      is_imperial: values.is_imperial,
      is_active: values.is_active,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Unit of Measure' : 'New Unit of Measure'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="uom"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>UoM Code *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., kg, lb, m" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="singular"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Singular *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., kilogram" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="plural"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Plural *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., kilograms" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4 pt-2">
              <FormField
                control={form.control}
                name="is_si"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal cursor-pointer">SI Unit</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_imperial"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal cursor-pointer">Imperial</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal cursor-pointer">Active</FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>

            {isEditing && (
              <RecordMetadata record={uom} />
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
